/* eslint-disable no-console */
/**
 * Modal de clasificación de una BOLETA DE HONORARIOS, para la tabla de Compras (Fase 3c).
 *
 * Pieza NUEVA a propósito: `AsignarRecibidaModal` está cableado a `facturas_sii_recibidas`
 * (su `handleAsignar` llama `vincularFacturaRecibida`, escribe `estado_vinculacion`, crea
 * activos, etc.). Una boleta no es un DTE recibido: se escribe directo en
 * `boletas_honorarios.cuenta_er` / `.tipo_contable`, columnas que ya existen desde Fase 1.
 *
 * Cierra el loop de Fases 1-2: hasta ahora las boletas entraban a `costosER` pero, sin cuenta,
 * caían en sin-clasificar para siempre. Con `cuenta_er` seteada devengan en su línea del ER
 * (la fuente ya está cableada, `costosER.js`); marcadas `activo` salen de la línea de costo
 * (el motor excluye `tipo_contable='activo'`).
 *
 * NO toca `computa` / `status` / `monto_*`: la clasificación es ortogonal a que la boleta
 * devengue o no (una nula clasificada sigue sin computar). El path completo activo→CAPEX
 * queda para después, como se acordó: acá solo se marca el tipo contable.
 */
import { useState } from "react";
import toast from "react-hot-toast";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button } from "reactstrap";
import { CUENTAS_ER } from "../../constants/clasificacionContable";
import { actualizarProveedor } from "../../utils/proveedores";
import { client } from "../../utils/supabase";

const CUENTAS_POR_GRUPO = [...new Set(CUENTAS_ER.map((c) => c.grupo))].map((grupo) => ({
  grupo,
  cuentas: CUENTAS_ER.filter((c) => c.grupo === grupo),
}));

const formatMonto = (monto) => (monto == null ? "-" : `$ ${Number(monto).toLocaleString("es-CL")}`);

const ClasificarHonorarioModal = ({ boleta, proveedor = null, onClose, onClasificada }) => {
  // `boleta` es la fila CRUDA de boletas_honorarios (el `raw` del doc — no se re-fetchea).
  const [tipoContable, setTipoContable] = useState(boleta.tipo_contable || "gasto");
  const [cuentaER, setCuentaER] = useState(boleta.cuenta_er || proveedor?.cuenta_er_default || "");
  const [recordar, setRecordar] = useState(false);
  const [guardando, setGuardando] = useState(false);

  const nombre = boleta.emisor_nombre || "Honorario";

  const handleGuardar = async () => {
    if (tipoContable === "gasto" && !cuentaER) {
      toast.error("Elegí una cuenta del ER para el gasto");
      return;
    }
    setGuardando(true);
    try {
      // Activo → cuenta_er en null (no tiene línea de costo; el motor la excluye por
      // tipo_contable). Gasto → la cuenta elegida. PostgREST NO lanza: se captura el {error}
      // (misma lección que 9.38 — un update fallido con toast.success engañoso).
      // Clasificación DELIBERADA per-documento → clasificacion_manual = true (override): no hay
      // auto-clasificación de honorarios, pero la marca deja el criterio homogéneo con recibidas.
      const cambios =
        tipoContable === "activo"
          ? { tipo_contable: "activo", cuenta_er: null, clasificacion_manual: true }
          : { tipo_contable: "gasto", cuenta_er: cuentaER, clasificacion_manual: true };
      const { error } = await client.from("boletas_honorarios").update(cambios).eq("id", boleta.id);
      if (error) throw error;

      // "Recordar" persiste el default al proveedor (por ID, regla 10.7), como recibidas.
      if (recordar && boleta.proveedor_id) {
        await actualizarProveedor(boleta.proveedor_id, {
          cuenta_er_default: tipoContable === "gasto" ? cuentaER : null,
          tipo_contable_default: tipoContable,
        });
      }

      toast.success("Boleta clasificada");
      onClasificada?.();
    } catch (err) {
      console.error("ClasificarHonorarioModal: error al guardar", err);
      toast.error("No se pudo guardar la clasificación");
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Modal isOpen toggle={onClose} size="md">
      <ModalHeader toggle={onClose}>
        Clasificar honorario — Boleta {boleta.folio} &middot; {nombre}
      </ModalHeader>
      <ModalBody>
        {/* Detalle compacto, una línea (mismo formato gris que el modal de recibidas). */}
        <div
          style={{
            background: "#f8f9fa",
            padding: "8px 14px",
            borderRadius: 6,
            marginBottom: 14,
            fontSize: "0.8rem",
            display: "flex",
            flexWrap: "wrap",
            gap: "4px 24px",
          }}
        >
          <span>
            Bruto: <strong>{formatMonto(boleta.monto_bruto)}</strong>
          </span>
          <span>
            Retención: <strong>{formatMonto(boleta.monto_retencion)}</strong>
          </span>
          <span>
            Fecha: <strong>{boleta.fecha_emision ? boleta.fecha_emision.substring(0, 10) : "-"}</strong>
          </span>
        </div>

        {/* La BHE es exenta de IVA: el bruto ES el costo. Recordatorio de que la retención no
            baja el gasto (es pasivo tributario, vive en el bloque F29 de Caja). */}
        <div style={{ fontSize: "0.72rem", color: "#909497", marginBottom: 14 }}>
          El bruto exento devenga como costo; la retención no baja el gasto (se entera al SII).
        </div>

        {/* Tipo contable. Default gasto, pero ~90% de los honorarios son instalación → activo. */}
        <div style={{ marginBottom: 12 }}>
          <label style={{ fontSize: "0.75rem", fontWeight: 600, display: "block", marginBottom: 4 }}>
            Tipo contable
          </label>
          <div style={{ display: "flex", gap: 16, fontSize: "0.82rem" }}>
            <label style={{ cursor: "pointer" }}>
              <input
                type="radio"
                name="tipoContableHon"
                value="gasto"
                checked={tipoContable === "gasto"}
                onChange={() => setTipoContable("gasto")}
                style={{ marginRight: 4 }}
              />
              Gasto (servicio)
            </label>
            <label style={{ cursor: "pointer" }}>
              <input
                type="radio"
                name="tipoContableHon"
                value="activo"
                checked={tipoContable === "activo"}
                onChange={() => setTipoContable("activo")}
                style={{ marginRight: 4 }}
              />
              Activo (instalación)
            </label>
          </div>
        </div>

        {tipoContable === "gasto" ? (
          <div style={{ marginBottom: 4 }}>
            <label style={{ fontSize: "0.75rem", fontWeight: 600, display: "block", marginBottom: 4 }}>
              Cuenta del Estado de Resultados
            </label>
            <select
              value={cuentaER}
              onChange={(e) => setCuentaER(e.target.value)}
              style={{
                width: "100%",
                height: 36,
                fontSize: "0.82rem",
                padding: "4px 10px",
                border: "1px solid #ced4da",
                borderRadius: 4,
              }}
            >
              <option value="">— Seleccionar cuenta —</option>
              {CUENTAS_POR_GRUPO.map(({ grupo, cuentas }) => (
                <optgroup key={grupo} label={grupo}>
                  {cuentas.map((c) => (
                    <option key={c.value} value={c.value}>
                      {c.label}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
          </div>
        ) : (
          // Sin path a CAPEX todavía (acordado): solo se marca. Se avisa para no dar a
          // entender que se creó un activo depreciable.
          <div
            style={{
              background: "#eaf2f8",
              color: "#2874a6",
              padding: "8px 12px",
              borderRadius: 6,
              fontSize: "0.78rem",
              marginBottom: 4,
            }}
          >
            Se marca como <strong>activo</strong> y sale de la línea de costo del ER. La depreciación / alta del activo
            se hace aparte.
          </div>
        )}

        {boleta.proveedor_id && (
          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: "0.78rem", marginTop: 12 }}>
            <input type="checkbox" checked={recordar} onChange={(e) => setRecordar(e.target.checked)} />
            Recordar para {nombre}
          </label>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" outline size="sm" onClick={onClose} disabled={guardando}>
          Cancelar
        </Button>
        <Button color="success" size="sm" style={{ minWidth: 100 }} onClick={handleGuardar} disabled={guardando}>
          {guardando ? "Guardando…" : "Guardar"}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default ClasificarHonorarioModal;
